import { GoogleReCaptchaProvider } from "@google-recaptcha/react";
import { FC, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { EvianErrorCheckPwd } from "../../components/evian/EvianErrorCheckPwd";
import { EvianFormHeader } from "../../components/evian/EvianFormHeader";
import { EvianRegisterForm } from "../../components/evian/EvianRegisterForm";
import { EvianSuccessScreen } from "../../components/evian/EvianSuccessScreen";
import { HardLoading } from "../../components/HardLoading";
import { API } from "../../utils/API";
import { EVIAN_COLORS } from "../../utils/Colors";
import { EvianSignUpData } from "../../models/Evian";

const radii = {
  md: "10px",
  lg: "14px",
  xl: "18px",
};

const shadow = {
  card: "0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.1)",
  inset: "inset 0 1px 0 rgba(255,255,255,0.4)",
};

const Page = styled.div`
  min-height: 100dvh;
  background: ${EVIAN_COLORS.bg};
  display: flex;
  justify-content: center;
  padding: 40px 16px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 760px;
  background: ${EVIAN_COLORS.panel};
  border: 1px solid ${EVIAN_COLORS.border};
  border-radius: ${radii.xl};
  box-shadow: ${shadow.card};
  padding: 28px;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: ${EVIAN_COLORS.border};
  margin: 10px 0 22px;
`;

export const EvianSweepstakesForm: FC = () => {
  const [searchParams] = useSearchParams();
  const pswd = searchParams.get('pw');
  const zone = searchParams.get('zone');

  const [isPswdZoneValid, setIsPswdZoneValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pwdErrorMessage, setPwdErrorMessage] = useState<string>("");
  const [isSuccessScreenVisible, setIsSuccessScreenVisible] = useState<boolean>(false);

  const [recaptchaProviderType, setRecaptchaProviderType] = useState<'v2-checkbox' | 'v3'>('v3');
  const [recaptchaProviderSiteKey, setRecaptchaProviderSiteKey] = useState<string>(process.env.REACT_APP_CAPTCHA_SITE_KEY_V3 as string);

  const [values, setValues] = useState<EvianSignUpData>({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    zone: zone || "",
    recaptcha_token: "",
    recaptcha_type: recaptchaProviderType.includes('v2') ? 'v2' : 'v3',
    agree: false,
  });

  const handleCheckPswdZone = async () => {
    try {
      const response = await API.Evian.checkPswdZone(pswd, zone);
      if (!response.valid) {
        setIsPswdZoneValid(false);
        setPwdErrorMessage(response.msg);
        return;
      }
      setIsPswdZoneValid(true);
    } catch (error: any) {
      setIsPswdZoneValid(false);
      setPwdErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    handleCheckPswdZone();
  }, []);

  if (isLoading) {
    return <HardLoading
      isVisible={isLoading} />
  }

  if (!isPswdZoneValid) {
    return <EvianErrorCheckPwd
      handleRetry={handleCheckPswdZone}
      errorMessage={pwdErrorMessage} />
  }

  if (isSuccessScreenVisible) {
    return <EvianSuccessScreen />
  }

  return (
    <GoogleReCaptchaProvider key={`${recaptchaProviderType}:${recaptchaProviderSiteKey}`} type={recaptchaProviderType} siteKey={recaptchaProviderSiteKey}>
      <Page>
        <Card>
          <EvianFormHeader />
          <Divider />
          <EvianRegisterForm
            zone={zone}
            values={values}
            setValues={setValues}
            setRecaptchaProviderType={setRecaptchaProviderType}
            setRecaptchaProviderSiteKey={setRecaptchaProviderSiteKey}
            setIsSuccessScreenVisible={setIsSuccessScreenVisible}
            recaptchaProviderType={recaptchaProviderType} />
        </Card>
      </Page>
    </GoogleReCaptchaProvider>
  );
}
